import { defineStore } from 'pinia';
import { DocBitsNotification } from 'src/components/DocBitsNotification/DocBitsNotification';
import IdeasApiService from 'src/services/ideas/IdeasApiService';
import { useUserStore } from 'src/store/User/user_details.store';
import type {
  Idea,
  IdeaAsset,
  IdeaCategory,
  IdeaChannel,
  IdeaComment,
  IdeaStatus,
  PaginationInfo,
  PendingComment,
  PendingIdea,
  TrustedOrg,
  CreateIdeaPayload,
  UpdateIdeaPayload,
  VoteDirection,
} from 'src/types/api/ideas';

const ideasApiService = new IdeasApiService();

function currentUserDisplayName(): string | undefined {
  const userStore = useUserStore();
  const firstName = String(userStore.getUserDetailByKey('first_name') ?? '').trim();
  const lastName = String(userStore.getUserDetailByKey('last_name') ?? '').trim();
  const fullName = [firstName, lastName].filter(Boolean).join(' ');
  return fullName || undefined;
}

export const useIdeasStore = defineStore('ideas', {
  state: () => ({
    ideas: [] as Idea[],
    loading: false,
    myIdeaVotes: {} as Record<string, 1 | -1>,
    myCommentVotes: {} as Record<string, 1 | -1>,
    pagination: {
      current_page: 1,
      page_size: 20,
      total_entries: 0,
      total_pages: 0,
      has_next_page: false,
    } as PaginationInfo,
    comments: {} as Record<string, IdeaComment[]>,
    assets: {} as Record<string, IdeaAsset[]>,
    hasModerationAccess: false,
    pendingComments: [] as PendingComment[],
    pendingIdeas: [] as PendingIdea[],
    trustedOrgs: [] as TrustedOrg[],
    filters: {
      category: null as IdeaCategory | null,
      channel: null as IdeaChannel | null,
      status: null as IdeaStatus | null,
      search: '',
      sort: 'newest' as string,
    },
    _voteInFlight: new Set<string>(),
  }),

  getters: {
    getIdeaVote: (state) => (ideaId: string): 1 | -1 | 0 => {
      return state.myIdeaVotes[ideaId] ?? 0;
    },
    getCommentVote: (state) => (commentId: string): 1 | -1 | 0 => {
      return state.myCommentVotes[commentId] ?? 0;
    },
  },

  actions: {
    _applyOptimisticVote<T extends { vote_count: number; upvote_count?: number; downvote_count?: number }>(
      entity: T,
      votes: Record<string, 1 | -1>,
      entityId: string,
      direction: VoteDirection,
    ): { previousVote: number; snapshot: { vote_count: number; upvote_count?: number; downvote_count?: number } } {
      const previousVote = votes[entityId] ?? 0;
      const numDirection = direction === 'up' ? 1 : -1;
      const snapshot = {
        vote_count: entity.vote_count,
        upvote_count: entity.upvote_count,
        downvote_count: entity.downvote_count,
      };

      const toggledOff = previousVote === numDirection;
      const wasUp = previousVote === 1 ? 1 : 0;
      const wasDown = previousVote === -1 ? 1 : 0;
      const nowUp = !toggledOff && numDirection === 1 ? 1 : 0;
      const nowDown = !toggledOff && numDirection === -1 ? 1 : 0;
      const upDelta = nowUp - wasUp;
      const downDelta = nowDown - wasDown;

      entity.vote_count += upDelta - downDelta;
      if (entity.upvote_count !== undefined) entity.upvote_count += upDelta;
      if (entity.downvote_count !== undefined) entity.downvote_count += downDelta;

      if (toggledOff) delete votes[entityId];
      else votes[entityId] = numDirection as 1 | -1;

      return { previousVote, snapshot };
    },

    _rollbackVote<T extends { vote_count: number; upvote_count?: number; downvote_count?: number }>(
      entity: T,
      votes: Record<string, 1 | -1>,
      entityId: string,
      previousVote: number,
      snapshot: { vote_count: number; upvote_count?: number; downvote_count?: number },
    ): void {
      entity.vote_count = snapshot.vote_count;
      if (entity.upvote_count !== undefined && snapshot.upvote_count !== undefined) {
        entity.upvote_count = snapshot.upvote_count;
      }
      if (entity.downvote_count !== undefined && snapshot.downvote_count !== undefined) {
        entity.downvote_count = snapshot.downvote_count;
      }
      if (previousVote === 0) {
        delete votes[entityId];
      } else {
        votes[entityId] = previousVote as 1 | -1;
      }
    },

    async loadIdeas(page?: number) {
      this.loading = true;
      try {
        const currentPage = page || this.pagination.current_page;
        const [listResult, votesResult] = await Promise.all([
          ideasApiService.getIdeas({
            page: currentPage,
            page_size: this.pagination.page_size,
            category: this.filters.category || undefined,
            channel: this.filters.channel || undefined,
            status: this.filters.status || undefined,
            search: this.filters.search || undefined,
            sort: this.filters.sort,
          }),
          ideasApiService.getMyVotes(),
        ]);

        if (listResult.success) {
          this.ideas = listResult.ideas;
          this.pagination = listResult.pagination;
        }

        if (votesResult.success) {
          this.myIdeaVotes = votesResult.idea_votes ?? {};
          this.myCommentVotes = votesResult.comment_votes ?? {};
        }
      } catch (error) {
        console.error('Failed to load ideas:', error);
      } finally {
        this.loading = false;
      }
    },

    async createIdea(payload: CreateIdeaPayload) {
      let result;
      try {
        result = await ideasApiService.createIdea({
          ...payload,
          creator_name: payload.creator_name ?? currentUserDisplayName(),
        });
      } catch (error) {
        console.error('Failed to create idea:', error);
        DocBitsNotification.error('Failed to create idea. Please try again.');
        throw error;
      }
      if (!result?.success) return undefined;

      try {
        this.ideas.unshift(result.idea);
        this.pagination.total_entries += 1;
        DocBitsNotification.success('Idea submitted successfully.');
      } catch (sideEffectError) {
        console.error('Idea created but post-create UI updates failed:', sideEffectError);
      }
      return result.idea;
    },

    async voteOnIdea(ideaId: string, direction: VoteDirection) {
      if (this._voteInFlight.has(ideaId)) return;
      this._voteInFlight.add(ideaId);

      const idea = this.ideas.find((i) => i.id === ideaId);
      if (!idea) { this._voteInFlight.delete(ideaId); return; }

      const { previousVote, snapshot } = this._applyOptimisticVote(
        idea, this.myIdeaVotes, ideaId, direction,
      );

      try {
        const result = await ideasApiService.voteOnIdea(ideaId, direction);
        if (result.success) {
          idea.vote_count = result.vote_count;
          idea.upvote_count = result.upvote_count;
          idea.downvote_count = result.downvote_count;
          if (result.voted && result.direction) {
            this.myIdeaVotes[ideaId] = result.direction === 'up' ? 1 : -1;
          } else {
            delete this.myIdeaVotes[ideaId];
          }
        }
      } catch (error) {
        this._rollbackVote(idea, this.myIdeaVotes, ideaId, previousVote, snapshot);
        console.error('Failed to vote on idea:', error);
        DocBitsNotification.error('Vote failed. Please try again.');
      } finally {
        this._voteInFlight.delete(ideaId);
      }
    },

    async updateStatus(ideaId: string, status: IdeaStatus) {
      const idea = this.ideas.find((i) => i.id === ideaId);
      if (!idea) return;

      const previousStatus = idea.status;
      idea.status = status;

      try {
        const result = await ideasApiService.updateStatus(ideaId, status);
        if (result?.success === false) {
          idea.status = previousStatus;
          DocBitsNotification.error('Failed to update status. Please try again.');
          return;
        }
        DocBitsNotification.success('Status updated.');
      } catch (error) {
        idea.status = previousStatus;
        console.error('Failed to update status:', error);
        DocBitsNotification.error('Failed to update status. Please try again.');
        throw error;
      }
    },

    async updateIdea(ideaId: string, payload: UpdateIdeaPayload) {
      try {
        const result = await ideasApiService.updateIdea(ideaId, payload);
        if (result.success) {
          const idea = this.ideas.find((i) => i.id === ideaId);
          if (idea) {
            if (payload.title !== undefined) idea.title = payload.title;
            if (payload.description !== undefined) idea.description = payload.description;
            if (payload.category !== undefined) idea.category = payload.category;
            if (payload.channel !== undefined) idea.channel = payload.channel;
          }
          return result.idea;
        }
      } catch (error) {
        console.error('Failed to update idea:', error);
        throw error;
      }
    },

    async loadComments(ideaId: string) {
      try {
        const result = await ideasApiService.getComments(ideaId);
        if (result.success) {
          this.comments[ideaId] = result.comments;
        }
        return result.comments;
      } catch (error) {
        console.error('Failed to load comments:', error);
        throw error;
      }
    },

    async addComment(ideaId: string, content: string) {
      try {
        const result = await ideasApiService.createComment(ideaId, {
          content,
          creator_name: currentUserDisplayName(),
        });
        if (result.success) {
          this.comments[ideaId] = result.comments;
          DocBitsNotification.success('Comment posted.');
        }
        return result.comments;
      } catch (error) {
        console.error('Failed to add comment:', error);
        DocBitsNotification.error('Failed to post comment. Please try again.');
        throw error;
      }
    },

    async removeComment(ideaId: string, commentId: string) {
      try {
        const result = await ideasApiService.deleteComment(ideaId, commentId);
        if (result.success) {
          this.comments[ideaId] = (this.comments[ideaId] || []).filter(
            (c) => c.id !== commentId,
          );
          DocBitsNotification.success('Comment deleted.');
        }
      } catch (error) {
        console.error('Failed to remove comment:', error);
        DocBitsNotification.error('Failed to delete comment.');
        throw error;
      }
    },

    async voteOnComment(ideaId: string, commentId: string, direction: VoteDirection) {
      if (this._voteInFlight.has(commentId)) return;
      this._voteInFlight.add(commentId);

      const comment = (this.comments[ideaId] || []).find((c) => c.id === commentId);
      if (!comment) { this._voteInFlight.delete(commentId); return; }

      const { previousVote, snapshot } = this._applyOptimisticVote(
        comment, this.myCommentVotes, commentId, direction,
      );

      try {
        const result = await ideasApiService.voteOnComment(ideaId, commentId, direction);
        if (result.success) {
          comment.vote_count = result.vote_count;
          if (result.voted && result.direction) {
            this.myCommentVotes[commentId] = result.direction === 'up' ? 1 : -1;
          } else {
            delete this.myCommentVotes[commentId];
          }
        }
      } catch (error) {
        this._rollbackVote(comment, this.myCommentVotes, commentId, previousVote, snapshot);
        console.error('Failed to vote on comment:', error);
        DocBitsNotification.error('Vote failed. Please try again.');
      } finally {
        this._voteInFlight.delete(commentId);
      }
    },

    async hideComment(commentId: string, ideaId?: string) {
      try {
        const result = await ideasApiService.moderateComment(commentId, 'hide');
        if (result.success) {
          // Update moderation_status so moderator still sees it with a "Hidden" badge
          const updateComment = (comments: IdeaComment[]) => {
            const comment = comments.find((c) => c.id === commentId);
            if (comment) comment.moderation_status = 'hidden';
          };
          if (ideaId && this.comments[ideaId]) {
            updateComment(this.comments[ideaId]);
          } else {
            for (const id of Object.keys(this.comments)) {
              updateComment(this.comments[id] || []);
            }
          }
          DocBitsNotification.success('Comment hidden from other users.');
        }
      } catch (error) {
        console.error('Failed to hide comment:', error);
        DocBitsNotification.error('Failed to hide comment.');
        throw error;
      }
    },

    async deleteIdea(ideaId: string) {
      try {
        const result = await ideasApiService.deleteIdea(ideaId);
        if (result.success) {
          this.ideas = this.ideas.filter((i) => i.id !== ideaId);
          this.pagination.total_entries = Math.max(
            0,
            this.pagination.total_entries - 1,
          );
          delete this.comments[ideaId];
          delete this.assets[ideaId];
          DocBitsNotification.success('Idea deleted.');
        }
      } catch (error) {
        console.error('Failed to delete idea:', error);
        DocBitsNotification.error('Failed to delete idea.');
        throw error;
      }
    },

    async loadAssets(ideaId: string) {
      try {
        const result = await ideasApiService.getAssetsByIdea(ideaId);
        if (result.success) {
          this.assets[ideaId] = result.assets;
        }
        return result.assets;
      } catch (error) {
        console.error('Failed to load assets:', error);
        throw error;
      }
    },

    async uploadAsset(ideaId: string, file: File) {
      try {
        const result = await ideasApiService.uploadAsset(file, ideaId);
        if (result.success) {
          await this.loadAssets(ideaId);
        }
        return result.asset;
      } catch (error) {
        console.error('Failed to upload asset:', error);
        DocBitsNotification.error('Failed to upload file.');
        throw error;
      }
    },

    async removeAsset(ideaId: string, assetId: string) {
      try {
        const result = await ideasApiService.deleteAsset(assetId);
        if (result.success) {
          this.assets[ideaId] = (this.assets[ideaId] || []).filter(
            (a) => a.id !== assetId,
          );
        }
      } catch (error) {
        console.error('Failed to remove asset:', error);
        throw error;
      }
    },

    // Moderation actions

    async checkModerationAccess() {
      try {
        const result = await ideasApiService.checkModerationAccess();
        this.hasModerationAccess = result.success && result.has_access;
      } catch {
        this.hasModerationAccess = false;
      }
    },

    async loadPendingComments() {
      try {
        const result = await ideasApiService.getPendingComments();
        if (result.success) {
          this.pendingComments = result.comments;
        }
      } catch (error) {
        console.error('Failed to load pending comments:', error);
      }
    },

    async moderateComment(commentId: string, action: 'approve' | 'reject' | 'hide') {
      try {
        const result = await ideasApiService.moderateComment(commentId, action);
        if (result.success) {
          this.pendingComments = this.pendingComments.filter((c) => c.id !== commentId);
          const actionLabel = action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'hidden';
          DocBitsNotification.success(`Comment ${actionLabel}.`);
        }
      } catch (error) {
        console.error('Failed to moderate comment:', error);
        DocBitsNotification.error('Failed to moderate comment.');
        throw error;
      }
    },

    async loadPendingIdeas() {
      try {
        const result = await ideasApiService.getPendingIdeas();
        if (result.success) {
          this.pendingIdeas = result.ideas;
        }
      } catch (error) {
        console.error('Failed to load pending ideas:', error);
      }
    },

    async moderateIdea(ideaId: string, action: 'approve' | 'reject') {
      try {
        const result = await ideasApiService.moderateIdea(ideaId, action);
        if (result.success) {
          this.pendingIdeas = this.pendingIdeas.filter((i) => i.id !== ideaId);
          // If approved, surface the idea in the main list (it's now visible
          // to everyone). If rejected, drop it from the visible list — same
          // shape as comment moderation.
          if (action === 'approve') {
            const existing = this.ideas.find((i) => i.id === ideaId);
            if (existing) {
              existing.moderation_status = 'approved';
            }
          } else {
            this.ideas = this.ideas.filter((i) => i.id !== ideaId);
          }
          const actionLabel = action === 'approve' ? 'approved' : 'rejected';
          DocBitsNotification.success(`Idea ${actionLabel}.`);
        }
      } catch (error) {
        console.error('Failed to moderate idea:', error);
        DocBitsNotification.error('Failed to moderate idea.');
        throw error;
      }
    },

    async loadTrustedOrgs() {
      try {
        const result = await ideasApiService.getTrustedOrgs();
        if (result.success) {
          this.trustedOrgs = result.trusted_orgs;
        }
      } catch (error) {
        console.error('Failed to load trusted orgs:', error);
      }
    },

    async addTrustedOrg(orgId: string, orgName: string) {
      try {
        const result = await ideasApiService.addTrustedOrg(orgId, orgName);
        if (result.success) {
          await this.loadTrustedOrgs();
        }
      } catch (error) {
        console.error('Failed to add trusted org:', error);
        throw error;
      }
    },

    async removeTrustedOrg(id: string) {
      try {
        const result = await ideasApiService.removeTrustedOrg(id);
        if (result.success) {
          this.trustedOrgs = this.trustedOrgs.filter((o) => o.id !== id);
        }
      } catch (error) {
        console.error('Failed to remove trusted org:', error);
        throw error;
      }
    },

    setFilter(key: 'category' | 'channel' | 'status' | 'search' | 'sort', value: string | null) {
      if (key === 'search') {
        this.filters.search = value || '';
      } else if (key === 'sort') {
        this.filters.sort = value || 'newest';
      } else {
        (this.filters as Record<string, unknown>)[key] = value;
      }
    },

    resetFilters() {
      this.filters.category = null;
      this.filters.channel = null;
      this.filters.status = null;
      this.filters.search = '';
      this.filters.sort = 'newest';
    },
  },
});
